import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "../components/layout";
import Avatar from "../components/common/Avatar";
import api from "../services/api";
import { toast } from "react-toastify";
import { getAssetUrl } from "../services/helper";

const PendingRequestsPage = () => {
    const queryClient = useQueryClient();
    const [page, setPage] = useState(1);
    const [allRequests, setAllRequests] = useState([]);

    // Fetch Pending Requests
    const { data: pageData, isLoading, error, isFetching } = useQuery({
        queryKey: ["pending-requests", page, 10],
        queryFn: () => api.fetchPendingRequests(page, 10),
        keepPreviousData: true,
    });

    const newRequests = pageData?.data || [];
    const pagination = pageData?.pagination || {};

    // Append new page data to accumulated list
    useEffect(() => {
        if (newRequests.length > 0) {
            setAllRequests((prev) => {
                const existingIds = new Set(prev.map((item) => item.request_id));
                const filteredNew = newRequests.filter((item) => !existingIds.has(item.request_id));
                return [...prev, ...filteredNew];
            });
        }
    }, [newRequests]);

    // Accept Follow Mutation
    // Accept Follow Mutation
    const acceptFollowMutation = useMutation({
        mutationFn: (userId) => api.acceptFollowRequest(userId),
        onSuccess: (response, userId) => {           // ← userId is the variable passed
            toast.success("Follow request accepted successfully");

            // Remove the accepted request from the list
            setAllRequests((prev) =>
                prev.filter((item) => item.user_id !== userId)
            );

            queryClient.invalidateQueries({ queryKey: ["pending-requests"] });
            queryClient.invalidateQueries({ queryKey: ["connected-contacts"] });
        },
    });
    const acceptJoinRoomMutation = useMutation({
        mutationFn: ({ roomId, userId }) => api.acceptJoinRoomRequest(roomId, userId),
        onSuccess: () => {
            toast.success("Room join request accepted successfully");
            onClose();

            queryClient.invalidateQueries({ queryKey: ["pending-requests"] });
        },
    });
    // Accept Mentorship Mutation
    const acceptMentorshipMutation = useMutation({
        mutationFn: (userId) => api.acceptMentorshipRequest(userId),
        onSuccess: (response, userId) => {           // ← userId is the variable passed
            toast.success("Mentorship request accepted successfully");

            // Remove the accepted request from the list
            setAllRequests((prev) =>
                prev.filter((item) => item.user_id !== userId)
            );

            queryClient.invalidateQueries({ queryKey: ["pending-requests"] });
            queryClient.invalidateQueries({ queryKey: ["connected-contacts"] });
        },
    });


    const handleAccept = (request) => {
        if (request.request_type === "mentorship") {
            acceptMentorshipMutation.mutate(request.user_id);
        }
        else if (request.request_type === "follow") {
            acceptFollowMutation.mutate(request.user_id);
        }
        else {
            console.log({ request });
            acceptJoinRoomMutation.mutate({
                roomId: request.room_id,
                userId: request.request_id,
            });
        }
    };

    const loadMore = () => {
        if (pagination.hasNext && !isFetching) {
            setPage((prev) => prev + 1);
        }
    };

    return (
        <DashboardLayout>
            <div className="h-[calc(100vh-56px)] w-full overflow-y-auto 
                      [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden 
                      py-6 px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12">

                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-[28px] sm:text-[32px] font-bold text-[#0F172A] font-body">
                        Pending Requests
                    </h1>
                    <p className="text-gray-500 mt-1">Follow and Mentorship requests</p>
                </div>

                {/* Content */}
                {isLoading && allRequests.length === 0 ? (
                    <div className="flex justify-center py-20">
                        <div className="w-10 h-10 border-4 border-[#1F3A5F] border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="text-center py-20 text-red-500">Failed to load requests. Please try again.</div>
                ) : allRequests.length === 0 ? (
                    <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
                        <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6 text-4xl">
                            👥
                        </div>
                        <h3 className="text-2xl font-semibold text-[#0F172A]">No Pending Requests</h3>
                        <p className="text-gray-500 mt-2">You don't have any follow or mentorship requests at the moment.</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {allRequests.map((person) => (
                            <div
                                key={person.request_id}
                                className="flex items-center gap-4 bg-white p-4 rounded-2xl border border-gray-100 hover:border-gray-200 transition-all"
                            >
                                <Avatar
                                    src={getAssetUrl(person.avatar)}
                                    alt={person.name}
                                    size={58}
                                    className="flex-shrink-0"
                                />

                                <div className="flex-1 min-w-0">
                                    <p className="text-[17px] font-semibold text-[#0F172A] truncate">
                                        {person.name}
                                    </p>
                                    <p className="text-sm text-gray-500 capitalize">
                                        {person.request_type === "mentorship"
                                            ? "Mentorship Request"
                                            : person.request_type === "follow"
                                                ? "Follow Request"
                                                : `Join Room Request (Room Name: ${person.room_name})`
                                        }
                                    </p>
                                </div>

                                <button
                                    onClick={() => handleAccept(person)}
                                    disabled={acceptFollowMutation.isPending || acceptMentorshipMutation.isPending || acceptJoinRoomMutation.isPending}
                                    className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${person.request_type === "mentorship"
                                        ? "bg-[#FEDC5A] text-[#7A5C00] hover:brightness-95"
                                        : "bg-[#1F3A60] text-white hover:bg-[#16304f]"
                                        }`}
                                >
                                    {person.request_type === "mentorship"
                                        ? "Accept Mentorship"
                                        : person.request_type === "follow"
                                            ? "Accept Follow"
                                            : "Approve Join Room"
                                    }
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Load More Button */}
                {pagination.hasNext && (
                    <div className="flex justify-center mt-10 mb-12">
                        <button
                            onClick={loadMore}
                            disabled={isFetching}
                            className="px-8 py-3 bg-gray-100 hover:bg-gray-200 rounded-full text-sm font-medium transition-colors disabled:opacity-70"
                        >
                            {isFetching ? "Loading more..." : "Load More"}
                        </button>
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default PendingRequestsPage;